// '@angular/localize/init' is loaded via the "polyfills" entry in angular.json.
// Importing it directly here as well makes Angular warn about undefined
// behaviour (NG: "Direct import of '@angular/localize/init' detected").
import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, Router } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { importProvidersFrom, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { init as SentryCapacitorInit } from '@sentry/capacitor/dist/esm/sdk';
import { browserTracingIntegration } from '@sentry/browser';
import { init as SentryAngularInit, createErrorHandler, TraceService } from '@sentry/angular';
import { toReportableError } from './utils/reportable-error';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { enableProdMode } from '@angular/core';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { LoggingInterceptor } from './interceptors/logging.interceptor';
import { authInitializerProvider, handlebarsInitializerProvider } from './app/app.initializer';
import { APP_INITIALIZER } from '@angular/core';
import { AppSettingsService } from './services/app-settings.service';
import { LoggerService } from './services/logger.service';
import { LogCollectorService } from './services/log-collector.service';
import { BILLING_SERVICE } from './services/billing-mock/billing.token';
import { BillingMockService } from './services/billing-mock/billing-mock.service';
import { BillingService } from './services/billing/billing.service';
import { installStaleDynamicImportRecovery, recoverStaleDynamicImport } from './utils/stale-dynamic-import-recovery';
import { resolveSentryEnvironment, shouldSendSentryEvents } from './utils/sentry-environment';

// Recover from stale lazy-chunk imports after a deployment (Bug 17427).
// Installed before Sentry/Angular so a rejected dynamic import in the
// a lazy route triggers one reload.
installStaleDynamicImportRecovery();

if (environment.production) {
  enableProdMode();
}

// ============================================================================
// Sentry Error Tracking Initialization
// Initialize Sentry as early as possible to capture startup errors
// Load config synchronously via XMLHttpRequest to get environment before Sentry init
// ============================================================================
const SENTRY_DSN = 'https://bbbf17e1a5acfb99404ec759e02105f5@o4510784553877504.ingest.us.sentry.io/4510794357211136';

// Try to load config synchronously for Sentry environment.
// The name mapping lives in sentry-environment.ts so tests do not import this file.
let configSentryEnvironment: string | null = null;
let configEnvironment: string | null = null;
try {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', './assets/config/app-config.json', false); // Synchronous request
  xhr.send();
  if (xhr.status === 200) {
    const config = JSON.parse(xhr.responseText);
    configSentryEnvironment = config.sentryEnvironment ?? null;
    configEnvironment = config.environment ?? null;
  }
} catch (e) {
  console.warn('[Sentry] Could not load config for environment, using build default');
}

const sentryEnvironment = resolveSentryEnvironment({
  productionBuild: environment.production,
  sentryEnvironment: configSentryEnvironment,
  environment: configEnvironment,
});

SentryCapacitorInit({
    dsn: SENTRY_DSN,
    enabled: shouldSendSentryEvents(sentryEnvironment),
    environment: sentryEnvironment,
    integrations: [
      browserTracingIntegration(),
    ],
    // Performance monitoring - capture 20% of transactions
    tracesSampleRate: 0.2,
    // Trace propagation for cross-origin requests
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.soloapps\.co/,
      /^https:\/\/.*\.azurewebsites\.net/
    ],
    // No Session Replay. Sentry deprecated it in @sentry/capacitor 3.2.1 and
    // REMOVED it in 4.0.0 - their docs state plainly that Replay is no longer
    // supported on this SDK, and CapacitorOptions Omits both sample rates.
    // Driving replayIntegration directly would be reaching around a deliberate
    // removal for an unsupported feature, at ~120 KB of initial bundle. If
    // replay matters, it needs a supported transport, not a workaround here.
  },
  // Forward the init method from @sentry/angular
  SentryAngularInit
);

console.log('[Sentry] Initialized for environment:', sentryEnvironment);

// App settings initializer - loads config before app starts
const appSettingsInitializer = {
  provide: APP_INITIALIZER,
  useFactory: (appSettings: AppSettingsService, logger: LoggerService) => () => appSettings.loadSettings().then(() => {
    logger.debug('Main', 'appSettingsInitializer', 'Settings loaded successfully');
  }),
  deps: [AppSettingsService, LoggerService],
  multi: true
};

// Log collector initializer - initializes the log collector service after settings are loaded
const logCollectorInitializer = {
  provide: APP_INITIALIZER,
  useFactory: (appSettings: AppSettingsService, logCollector: LogCollectorService, logger: LoggerService) => async () => {
    // Wait for settings to load (returns cached if already loaded)
    const settings = await appSettings.loadSettings();
    const logCollectorConfig = settings.logCollector ?? { enabled: true };
    logCollector.initialize(logCollectorConfig, settings.apiUrl);
    logger.debug('Main', 'logCollectorInitializer', 'Log collector initialized', {
      enabled: logCollectorConfig.enabled ?? true,
      endpoint: logCollector.getEndpoint()
    });
  },
  deps: [AppSettingsService, LogCollectorService, LoggerService],
  multi: true
};


// Detect platform to determine animation configuration
const platform = Capacitor.getPlatform();
const isWeb = platform === 'web';

// Configure Ionic based on platform
// For web: disable animations to prevent slide-up effect
// For mobile (ios/android): keep default animations
const ionicConfig = isWeb ? { animated: false } : {};

// Platform detection information is already logged via LoggerService in app initializers

// Providers array
const providers = [
  { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  // Stale chunk loads reload once and skip Sentry. A second miss, a native
  // shell, and every other error still reach the Sentry handler.
  {
    provide: ErrorHandler,
    useFactory: () => {
      const sentryHandler = createErrorHandler({
        extractor: (error, defaultExtractor) => toReportableError(defaultExtractor(error))
      });
      return {
        handleError(error: unknown): void {
          if (recoverStaleDynamicImport(error) === 'reloaded') {
            return;
          }
          sentryHandler.handleError(error);
        },
      };
    },
  },
  // Sentry TraceService - enables performance tracing
  { provide: TraceService, deps: [Router] },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {},
    deps: [TraceService],
    multi: true,
  },
  provideIonicAngular(ionicConfig),
  provideRouter(routes),
  provideHttpClient(withInterceptorsFromDi()),
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  appSettingsInitializer, // Load settings first
  logCollectorInitializer, // Initialize log collector after settings
  authInitializerProvider, // Then auth
  handlebarsInitializerProvider, // Register Handlebars helpers
  {
    provide: BILLING_SERVICE,
    useExisting: environment.useBillingMockService ? BillingMockService : BillingService
  }
];

bootstrapApplication(AppComponent, {
  providers: [provideZoneChangeDetection(), ...providers]
});
